<template>
  <div class="card mb-25 border-0 rounded-0 bg-white edit-composition-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form
        ref="compositionForm"
        @submit="editComposition"
        @invalid-submit="handleInvalidSubmit"
        :validation-schema="compositionSchema"
        :validateOnInput="false"
        :validateOnMount="false"
        :initialValues="{
          titre: titre,
          dateComposition: dateComposition,
          dateFinSoumission: dateFinSoumission,
          dureeMinutes: dureeMinutes,
          professeur: selectedProfesseur,
          niveau: selectedNiveau,
          matiere: selectedMatiere,
          filiere: selectedFiliere,
          anneeAcademique: selectedAnneeAcademique,
          typeComposition: typeComposition,
          questions: selectedQuestions,
          questionsAdded: addedQuestions
        }"
      >

        <!-- Ligne 1 : Titre + Date composition + Date limite soumission -->
        <div class="row">
          <div class="col-md-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Titre <span class="text-danger">*</span>
              </label>
              <Field name="titre" type="text" v-model="titre"
                class="form-control shadow-none fs-md-15 text-black" placeholder="Entrer le titre"/>
              <ErrorMessage name="titre" class="text-danger"/>
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date de composition <span class="text-danger">*</span>
              </label>
              <Field name="dateComposition" type="date" v-model="dateComposition"
                class="form-control shadow-none fs-md-15 text-black"/>
              <ErrorMessage name="dateComposition" class="text-danger"/>
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date limite de soumission <span class="text-danger">*</span>
              </label>
              <Field name="dateFinSoumission" type="date" v-model="dateFinSoumission"
                class="form-control shadow-none fs-md-15 text-black"/>
              <ErrorMessage name="dateFinSoumission" class="text-danger"/>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Durée (minutes) <span class="text-danger">*</span>
              </label>
              <Field name="dureeMinutes" type="number" min="1"
                class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 45"/>
              <ErrorMessage name="dureeMinutes" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Ligne 2 : Niveau + Filière + Matière -->
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Niveau <span class="text-danger">*</span>
              </label>
              <Field name="niveau" v-slot="{ field }">
                <Multiselect
                  :options="niveauxOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onNiveauChange"
                  placeholder="Sélectionner le niveau"
                />
              </Field>
              <ErrorMessage name="niveau" class="text-danger"/>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Filière <span class="text-danger">*</span>
              </label>
              <Field name="filiere" v-slot="{ field }">
                <Multiselect
                  :options="filieresOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onFiliereChange"
                  placeholder="Sélectionner la filière"
                  :disabled="!selectedNiveau || isLoadingFilieres"
                />
              </Field>
              <ErrorMessage name="filiere" class="text-danger"/>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Matière <span class="text-danger">*</span>
              </label>
              <Field name="matiere" v-slot="{ field }">
                <Multiselect
                  :options="matieresOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onMatiereChange"
                  placeholder="Sélectionner la matière"
                  :disabled="!selectedNiveau || !selectedFiliere || isLoadingMatieres"
                />
              </Field>
              <ErrorMessage name="matiere" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Ligne 3 : Professeur + Année Académique + Type de composition -->
        <div class="row">
          <!-- Professeur -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Professeur <span class="text-danger">*</span>
              </label>
              <Field name="professeur" v-slot="{ field }">
                <Multiselect
                  :options="currentProfesseursOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  v-bind="field"
                  placeholder="Sélectionner le professeur"
                  :disabled="!selectedMatiere || !currentProfesseursOptions.length"
                />
              </Field>
              <ErrorMessage name="professeur" class="text-danger"/>
            </div>
          </div>

          <!-- Année Académique -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Année Académique <span class="text-danger">*</span>
              </label>
              <Field name="anneeAcademique" v-slot="{ field }">
                <Multiselect
                  :options="anneesAcademiquesOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  v-model="field.value"
                  v-bind="field"
                  placeholder="Sélectionner l'année académique"/>
              </Field>
              <ErrorMessage name="anneeAcademique" class="text-danger"/>
            </div>
          </div>

          <!-- Type de composition (compact, même ligne) -->
          <div class="col-md-4">
            <div class="form-group mb-3">
              <label class="d-block text-black fw-semibold mb-10">
                Type de composition <span class="text-danger">*</span>
              </label>
              <div class="d-flex align-items-center">
                <div class="form-check me-4">
                  <Field name="typeComposition" type="radio" value="QCM" id="qcmRadio" 
                         @change="handleTypeChange('QCM')" class="form-check-input me-2"/>
                  <label class="form-check-label fw-medium" for="qcmRadio">QCM</label>
                </div>
                <div class="form-check">
                  <Field name="typeComposition" type="radio" value="QuestionsReponses" id="qrRadio" 
                         @change="handleTypeChange('QuestionsReponses')" class="form-check-input me-2"/>
                  <label class="form-check-label fw-medium" for="qrRadio">Questions/Réponses</label>
                </div>
              </div>
              <ErrorMessage name="typeComposition" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Section Questions QCM - Coins droits + QCM en blanc -->
        <div v-if="typeComposition === 'QCM'" class="row mt-4">
          <div class="col-md-12">
            <div class="border border-primary mb-10">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-3 bg-primary">
                <div class="col-md-7 ps-4">
                  <h4 class="fs-4 text-white mb-0">QCM</h4>
                </div>
                <div class="col-md-5 pe-4">
                  <div class="badge bg-light text-primary fs-6 float-end" v-if="selectedQuestions.length > 0">
                    {{ selectedQuestions.length }} question(s) sélectionnée(s)
                  </div>
                </div>
              </div>

              <div class="p-4">
                <div class="form-group mb-15">
                  <label class="d-block text-black fw-semibold mb-10">
                    Sélectionner les questions <span class="text-danger">*</span>
                  </label>
                  <Field name="questions" v-slot="{ field }">
                    <Multiselect
                      :options="questionsOptions"
                      :searchable="true"
                      multiple="true"
                      mode="tags"
                      track-by="label"
                      label="label"
                      v-model="field.value"
                      v-bind="field"
                      @change="onQCMQuestionsChange"
                      placeholder="Sélectionner les questions"
                    />
                  </Field>
                  <ErrorMessage name="questions" class="text-danger" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Section Questions/Réponses -->
        <div v-if="typeComposition === 'QuestionsReponses'" class="row mt-4">
          <div class="col-md-12">
            <div class="border border-primary mb-10">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-primary">
                <div class="col-md-7 ps-3">
                  <h4 class="fs-4 text-white">Ajouter les questions</h4>
                </div>
                <div class="col-md-5 pe-3">
                  <button class="btn btn-success float-end" type="button" @click="addNewQuestion">
                    <i class="fa fa-plus-circle me-1"></i> Ajouter une question
                  </button>
                </div>
              </div>

              <div class="p-4">
                <div v-for="(question, index) in addedQuestions" :key="question.tempId" v-show="!question.toDelete">
                  <div class="row mt-3 align-items-start">
                    <div class="col-md-6">
                      <label class="d-block text-black fw-semibold mb-2">
                        Question {{ index + 1 }} <span class="text-danger">*</span>
                      </label>
                      <textarea
                        v-model="question.contenu"
                        class="form-control shadow-none text-black"
                        placeholder="Entrer le contenu de la question"
                        rows="3"
                      ></textarea>
                    </div>

                    <div class="col-md-4">
                      <label class="d-block text-black fw-semibold mb-2">Réponse</label>
                      <textarea
                        v-model="question.reponse"
                        class="form-control shadow-none text-black"
                        placeholder="Entrer la réponse (optionnel)"
                        rows="3"
                      ></textarea>
                    </div>

                    <div class="col-md-2 d-flex align-items-start pt-4">
                      <button
                        class="btn btn-danger btn-sm"
                        type="button"
                        @click="removeQuestion(index)"
                        v-if="addedQuestions.filter(q => !q.toDelete).length > 1"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <hr class="mt-0">
            </div>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="row mt-4">
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isSubmitting ? 'Modification...' : 'Modifier la composition' }}
              </button>
              <router-link
                to="/liste-compositions"
                class="btn btn-danger transition border-0 lh-1 fw-medium"
              >
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i>
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { success, error, showModal, hideModal } from "../../utils/utils";
import { useRouter, useRoute } from "vue-router";
import ApiService from "../../services/ApiService";
import * as Yup from "yup";
import Multiselect from '@vueform/multiselect';
import { Composition, Professeur, Niveau, Matiere, Filiere, Question } from "../../models/Composition";

interface Option {
  value: string | number;
  label: string;
}

interface MatiereOption extends Option {
  coefficient: number;
  matiereId: number;
  matiere: any;
  niveauId: number;
  niveau: any;
  filiereId: number;
}

export default defineComponent({
  name: "EditComposition",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    const compositionSchema = Yup.object().shape({
      titre: Yup.string().required("Le titre est obligatoire"),
      dateComposition: Yup.date().required("La date de composition est obligatoire"),
      dateFinSoumission: Yup.date().required("La date de fin de soumission est obligatoire"),
      dureeMinutes: Yup.number()
        .typeError("La durée doit être un nombre")
        .positive("La durée doit être supérieure à 0")
        .integer("La durée doit être un nombre entier")
        .required("La durée de la composition est obligatoire"),
      professeur: Yup.number().nullable().required("Le professeur est obligatoire"),
      niveau: Yup.number().nullable().required("Le niveau est obligatoire"),
      matiere: Yup.number().nullable().required("La matière est obligatoire"),
      filiere: Yup.number().nullable().required("La filière est obligatoire"),
      anneeAcademique: Yup.number().nullable().required("L'année académique est obligatoire"),
      typeComposition: Yup.string().required("Veuillez sélectionner un type de composition"),
      questions: Yup.array().when("typeComposition", {
        is: "QCM",
        then: (schema) => schema.min(1, "Veuillez sélectionner au moins une question").required(),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
      questionsAdded: Yup.array().when("typeComposition", {
        is: "QuestionsReponses",
        then: (schema) => schema.min(1, "Veuillez ajouter au moins une question").required(),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    });

    const compositionForm = ref<any>(null);
    const router = useRouter();
    const route = useRoute();
    const composition = ref<Composition | null>(null);
    const titre = ref("");
    const dateComposition = ref("");
    const dateFinSoumission = ref("");
    const dureeMinutes = ref<number | null>(null);
    const typeComposition = ref<"QCM" | "QuestionsReponses">("QCM");
    
    const niveauxOptions = ref<Option[]>([]);
    const filieresOptions = ref<Option[]>([]);
    const matieresOptions = ref<MatiereOption[]>([]);
    const selectedNiveau = ref<number | null>(null);
    const selectedFiliere = ref<number | null>(null);
    const selectedMatiere = ref<number | null>(null);
    const selectedMatiereOption = ref<MatiereOption | null>(null);
    const isLoadingMatieres = ref(false);
    const isLoadingFilieres = ref(false);
    
    const professeursParMatiere = ref<Map<number, Option[]>>(new Map());
    const professeursOptions = ref<Option[]>([]);
    
    const currentMatiereId = computed(() => {
      return selectedMatiereOption.value?.matiereId ?? null;
    });

    const currentProfesseursOptions = computed(() => {
      return getProfesseursByMatiere(currentMatiereId.value);
    });
    
    const anneesAcademiquesOptions = ref<any[]>([]);
    const questionsOptions = ref<any[]>([]);
    const selectedQuestions = ref<number[]>([]);
    const addedQuestions = ref<{ contenu: string; reponse?: string; id?: number; tempId: number; toDelete?: boolean }[]>([]);
    const updatedComposition = ref<Composition | null>(null);
    const isSubmitting = ref(false);
    const selectedProfesseur = ref<number | null>(null);
    const selectedAnneeAcademique = ref<number | null>(null);
    let tempQuestionIdCounter = 1;

    const handleInvalidSubmit = ({ errors }: any) => {
      console.error("Soumission invalide, erreurs :", JSON.stringify(errors, null, 2));
      error("Veuillez corriger les erreurs dans le formulaire");
    };

    onMounted(() => {
      fetchNiveaux();
      fetchProfesseursParMatiere();
      fetchAnneesAcademiques();
      fetchQuestions();
      
      if (route.params.id) {
        fetchComposition(parseInt(route.params.id as string));
      }
    });

    // Fonction fetchComposition corrigée pour charger TOUTES les données
const fetchComposition = async (id: number) => {
  try {
    const response = await ApiService.get(`/compositions/${id}`);
    composition.value = response.data.data;
    const compo = response.data.data;

    // Champs de base
    titre.value = compo.titre;
    dateComposition.value = compo.dateComposition.split("T")[0];
    dateFinSoumission.value = compo.dateFinSoumission ? compo.dateFinSoumission.split("T")[0] : "";
    dureeMinutes.value = compo.dureeMinutes;
    selectedAnneeAcademique.value = compo.annee?.id;
    typeComposition.value = compo.type || compo.typeComposition;

    // Chargement complet de niveau/filière/matière
    if (compo.filiereNiveauMatiere) {
      const fnm = compo.filiereNiveauMatiere;
      
      // Charger les niveaux
      await fetchNiveaux();
      
      // Définir le niveau ET mettre à jour le formulaire
      selectedNiveau.value = fnm.niveau?.id;
      await compositionForm.value?.setFieldValue('niveau', selectedNiveau.value);
      
      // Charger les filières pour ce niveau
      await fetchFilieresParNiveau(selectedNiveau.value);
      
      // Définir la filière ET mettre à jour le formulaire
      selectedFiliere.value = fnm.filiere?.id;
      await compositionForm.value?.setFieldValue('filiere', selectedFiliere.value);
      
      // Charger les matières pour ce niveau+filière
      await fetchMatieresFiltered(selectedNiveau.value, selectedFiliere.value);
      
      // Définir la matière ET mettre à jour le formulaire
      selectedMatiereOption.value = matieresOptions.value.find(opt => opt.matiereId === fnm.matiere?.id) || null;
      selectedMatiere.value = selectedMatiereOption.value?.value != null ? Number(selectedMatiereOption.value.value) : null;
      await compositionForm.value?.setFieldValue('matiere', selectedMatiere.value);
      
      console.log(' Champs chargés:', {
        niveau: selectedNiveau.value,
        filiere: selectedFiliere.value,
        matiere: selectedMatiere.value
      });
    }

    //  Chargement des questions ET réponses
    selectedQuestions.value = [];
    addedQuestions.value = [];

    if (typeComposition.value === "QCM" && compo.compoQuestions) {
      selectedQuestions.value = compo.compoQuestions.map((q: any) => q.question.id);
      await compositionForm.value?.setFieldValue('questions', selectedQuestions.value);
    } 
    else if (typeComposition.value === "QuestionsReponses" && compo.compoQuestions) {
      //  Construction d'une map des réponses
      const reponsesMap = new Map<number, string>();

      // Récupérer TOUTES les réponses depuis compo.reponses
      if (compo.reponses && Array.isArray(compo.reponses)) {
        compo.reponses.forEach((rep: any) => {
          if (rep.question?.id && rep.contenu !== null && rep.contenu !== undefined) {
            reponsesMap.set(rep.question.id, rep.contenu);
          }
        });
      }

      //Si les réponses sont dans question.reponses
      compo.compoQuestions.forEach((cq: any) => {
        if (cq.question?.reponses && Array.isArray(cq.question.reponses)) {
          cq.question.reponses.forEach((rep: any) => {
            if (rep.contenu !== null && rep.contenu !== undefined) {
              reponsesMap.set(cq.question.id, rep.contenu);
            }
          });
        }
      });

      console.log(' Réponses chargées:', Array.from(reponsesMap.entries()));
      
      // Construire les questions avec leurs réponses
      addedQuestions.value = compo.compoQuestions.map((cq: any) => {
        const question = cq.question;
        const reponse = reponsesMap.get(question.id) || "";
        
        return {
          id: question.id,
          contenu: question.contenu || "",
          reponse: reponse,
          tempId: tempQuestionIdCounter++,
        };
      });
      
      console.log(' Questions avec réponses:', addedQuestions.value);
      
      await compositionForm.value?.setFieldValue('questionsAdded', addedQuestions.value);
    }

    // Mettre à jour les autres champs du formulaire
    await compositionForm.value?.setFieldValue('titre', titre.value);
    await compositionForm.value?.setFieldValue('dateComposition', dateComposition.value);
    await compositionForm.value?.setFieldValue('dateFinSoumission', dateFinSoumission.value);
    await compositionForm.value?.setFieldValue('dureeMinutes', dureeMinutes.value); 
    await compositionForm.value?.setFieldValue('professeur', selectedProfesseur.value);
    await compositionForm.value?.setFieldValue('anneeAcademique', selectedAnneeAcademique.value);
    await compositionForm.value?.setFieldValue('typeComposition', typeComposition.value);
    
  } catch (err: unknown) {
    console.error(" Erreur lors de la récupération de la composition", err);
    error("Erreur lors de la récupération de la composition");
  }
};

    const fetchNiveaux = async () => {
      try {
        const response = await ApiService.get('/all/niveaux?limit=0');
        const niveauxData = response.data.data.data;
        niveauxOptions.value = niveauxData.map((niveau: any) => ({
          value: niveau.id,
          label: niveau.libelle,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des niveaux", err);
        error("Impossible de charger les niveaux");
      }
    };

    const fetchFilieresParNiveau = async (niveauId: number | null) => {
      if (!niveauId || typeof niveauId !== 'number') {
        filieresOptions.value = [];
        error("Veuillez sélectionner un niveau pour charger les filières.");
        return;
      }

      isLoadingFilieres.value = true;
      try {
        const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&limit=0`);
        if (response.data.code === 200) {
          const filiereNiveauMatieres = response.data.data.data;
          if (filiereNiveauMatieres.length === 0) {
            error("Aucune filière trouvée pour ce niveau.");
            filieresOptions.value = [];
            return;
          }
          const filieresUniques = Array.from(new Set(filiereNiveauMatieres.map((fnm: any) => fnm.filiere.id)))
            .map(id => filiereNiveauMatieres.find((fnm: any) => fnm.filiere.id === id).filiere);
          filieresOptions.value = filieresUniques.map((filiere: any) => ({
            value: filiere.id,
            label: filiere.libelle,
          }));
        } else {
          filieresOptions.value = [];
          error("Erreur lors de la récupération des filières.");
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des filières filtrées", err);
        filieresOptions.value = [];
        error("Impossible de charger les filières pour ce niveau.");
      } finally {
        isLoadingFilieres.value = false;
      }
    };

    const fetchAnneesAcademiques = async () => {
      try {
        const response = await ApiService.get('/all/annees?limit=0');
        const anneesData = response.data.data.data;
        anneesAcademiquesOptions.value = anneesData.map((annee: any) => ({
          value: annee.id,
          label: `${annee.libelle}`,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des années académiques", err);
      }
    };

    const fetchQuestions = async () => {
      try {
        const response = await ApiService.get('/questions?limit=0');
        const questionsData = response.data.data.data;
        questionsOptions.value = questionsData.map((question: Question) => ({
          value: question.id,
          label: question.contenu,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des questions", err);
      }
    };

    const fetchProfesseursParMatiere = async () => {
      try {
        const responseMatieres = await ApiService.get('/filiereNiveauMatieres?limit=0');
        
        if (responseMatieres.data?.code === 200 && Array.isArray(responseMatieres.data.data?.data)) {
          const toutesLesMatieres = responseMatieres.data.data.data;
          
          const matiereIdsSet = new Set<number>();
          toutesLesMatieres.forEach((fnm: any) => {
            if (fnm?.matiere?.id && typeof fnm.matiere.id === 'number' && fnm.matiere.id > 0) {
              matiereIdsSet.add(fnm.matiere.id);
            }
          });
          
          const matiereIds: number[] = Array.from(matiereIdsSet);

          if (matiereIds.length === 0) {
            console.warn('Aucune matière valide trouvée');
            return;
          }

          const professeurPromises = matiereIds.map(async (matiereId: number) => {
            try {
              const responseProfesseurs = await ApiService.get(`/professeurs/matiere/${matiereId}?limit=0`);
              
              if (responseProfesseurs.data?.code === 200 && Array.isArray(responseProfesseurs.data.data?.data)) {
                const professeursData = responseProfesseurs.data.data.data;
                const professeursOptions = professeursData
                  .map((prof: any) => ({
                    value: Number(prof.id),
                    label: `${prof.nom || ''} ${prof.prenom || ''}`.trim(),
                    matiereId: matiereId,
                    email: prof.email,
                    npi: prof.npi
                  }))
                  .filter((prof: any) => prof.value > 0 && prof.label && prof.label !== '');
                
                professeursParMatiere.value.set(matiereId, professeursOptions);
                return { matiereId, success: true, count: professeursOptions.length };
              } else {
                professeursParMatiere.value.set(matiereId, []);
                return { matiereId, success: false, error: 'API response invalid' };
              }
            } catch (err: any) {
              if (err.response?.status === 404) {
                professeursParMatiere.value.set(matiereId, []);
                return { matiereId, success: true, count: 0, empty: true };
              }
              
              professeursParMatiere.value.set(matiereId, []);
              return { matiereId, success: false, error: err.message };
            }
          });

          await Promise.all(professeurPromises);
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des professeurs par matière", err);
      }
    };

    const getProfesseursByMatiere = (matiereId: number | null | undefined): Option[] => {
      if (!matiereId || typeof matiereId !== 'number' || matiereId <= 0) {
        return [];
      }
      return professeursParMatiere.value.get(matiereId) || [];
    };

    const fetchMatieresFiltered = async (niveauId: number | null, filiereId: number | null) => {
      if (!niveauId || !filiereId || typeof niveauId !== 'number' || typeof filiereId !== 'number') {
        matieresOptions.value = [];
        error("Veuillez sélectionner un niveau et une filière pour charger les matières.");
        return;
      }

      isLoadingMatieres.value = true;
      try {
        const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&filiere=${filiereId}&limit=0`);
        if (response.data.code === 200) {
          const filiereNiveauMatieres = response.data.data.data;
          if (filiereNiveauMatieres.length === 0) {
            error("Aucune matière trouvée pour cette combinaison niveau-filière.");
            matieresOptions.value = [];
            return;
          }

          // ✅ Déduplication par matiere.id, alignée avec AddComposition.vue
          const matieresUniques = Array.from(
            new Map(filiereNiveauMatieres.map((fnm: any) => [fnm.matiere.id, fnm])).values()
          );

          matieresOptions.value = matieresUniques.map((fnm: any) => ({
            value: fnm.id,
            label: `${fnm.matiere.libelle}`,
            coefficient: fnm.coefficient,
            matiereId: fnm.matiere.id,
            matiere: fnm.matiere,
            niveauId: fnm.niveau.id,
            niveau: fnm.niveau,
            filiereId: fnm.filiere.id
          }));
        } else {
          matieresOptions.value = [];
          error("Erreur lors de la récupération des matières.");
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des matières", err);
        matieresOptions.value = [];
        error("Impossible de charger les matières.");
      } finally {
        isLoadingMatieres.value = false;
      }
    };

    const onNiveauChange = async (niveauValue: any) => {
      // Gestion correcte du changement de niveau
      const newNiveauId = typeof niveauValue === 'object' ? niveauValue?.value : niveauValue;
      
      // Si le niveau est désélectionné (null ou undefined)
      if (!newNiveauId) {
        selectedNiveau.value = null;
        selectedFiliere.value = null;
        selectedMatiere.value = null;
        selectedMatiereOption.value = null;
        filieresOptions.value = [];
        matieresOptions.value = [];
        await compositionForm.value?.setFieldValue('filiere', null);
        await compositionForm.value?.setFieldValue('matiere', null);
        await compositionForm.value?.setFieldValue('professeur', null);
        return;
      }

      selectedNiveau.value = newNiveauId;
      selectedFiliere.value = null;
      selectedMatiere.value = null;
      selectedMatiereOption.value = null;
      filieresOptions.value = [];
      matieresOptions.value = [];
      await compositionForm.value?.setFieldValue('filiere', null);
      await compositionForm.value?.setFieldValue('matiere', null);
      await compositionForm.value?.setFieldValue('professeur', null);

      await fetchFilieresParNiveau(selectedNiveau.value);
    };

    const onFiliereChange = async (filiereValue: any) => {
      selectedFiliere.value = filiereValue;
      
      selectedMatiere.value = null;
      selectedMatiereOption.value = null;
      matieresOptions.value = [];
      await compositionForm.value?.setFieldValue('matiere', null);
      await compositionForm.value?.setFieldValue('professeur', null);

      if (filiereValue && selectedNiveau.value) {
        await fetchMatieresFiltered(selectedNiveau.value, filiereValue);
      }
    };

    const onMatiereChange = async (matiereValue: any) => {
      selectedMatiere.value = matiereValue;
      selectedMatiereOption.value = matieresOptions.value.find(opt => opt.value === matiereValue) || null;
      
      await compositionForm.value?.setFieldValue('professeur', null);
    };

    const handleTypeChange = async (type: "QCM" | "QuestionsReponses") => {
      typeComposition.value = type;
      if (type === "QCM") {
        addedQuestions.value = [];
        await compositionForm.value?.setFieldValue("questionsAdded", []);
        await compositionForm.value?.setFieldValue("questions", selectedQuestions.value);
      } else {
        selectedQuestions.value = [];
        await compositionForm.value?.setFieldValue("questions", []);
        await compositionForm.value?.setFieldValue("questionsAdded", addedQuestions.value);
        if (addedQuestions.value.length === 0) {
          addNewQuestion();
        }
      }
    };

    const onQCMQuestionsChange = (selected: any[]) => {
      selectedQuestions.value = selected || [];
    };

    const addNewQuestion = async () => {
      try {
        const newQuestion = {
          contenu: "",
          reponse: "",
          tempId: tempQuestionIdCounter++,
        };
        addedQuestions.value.push(newQuestion);
        await compositionForm.value?.setFieldValue('questionsAdded', addedQuestions.value);
      } catch (err: unknown) {
        console.error("Erreur lors de l'ajout de la question:", err);
        error("Erreur lors de l'ajout de la question");
      }
    };

    const removeQuestion = async (index: number) => {
      const questionToRemove = addedQuestions.value[index];
      if (questionToRemove.id) {
        questionToRemove.toDelete = true;
      } else {
        addedQuestions.value.splice(index, 1);
      }
      await compositionForm.value?.setFieldValue('questionsAdded', addedQuestions.value);
    };

    const editComposition = async (values: any, { resetForm }: any) => {
      isSubmitting.value = true;

      try {
        const filiereNiveauMatiereId = values.matiere;

        const payload: any = {
          titre: values.titre,
          dateComposition: values.dateComposition,
          dateFinSoumission: values.dateFinSoumission,
          dureeMinutes: Number(values.dureeMinutes),
          professeur: { id: values.professeur },
          annee: { id: values.anneeAcademique },
          type: values.typeComposition,
          filiereNiveauMatiere: { id: filiereNiveauMatiereId },
        };

        if (values.typeComposition === 'QCM' && values.questions?.length > 0) {
          payload.compoQuestions = values.questions.map((questionId: number) => ({
            question: { id: questionId },
          }));
        } else if (values.typeComposition === 'QuestionsReponses') {
          if (addedQuestions.value.length === 0) {
            throw new Error("Aucune question ajoutée pour le type Questions/Réponses");
          }

          payload.questionsToCreate = addedQuestions.value
            .filter(q => !q.id && q.contenu.trim())
            .map(q => ({
              contenu: q.contenu,
              type: 'QuestionsReponses',
              reponse: q.reponse || null
            }));

          payload.questionsToUpdate = addedQuestions.value
            .filter(q => q.id && !q.toDelete)
            .map(q => ({
              id: q.id,
              contenu: q.contenu,
              reponse: q.reponse || null
            }));

          payload.questionsToDelete = addedQuestions.value
            .filter(q => q.id && q.toDelete)
            .map(q => q.id);

          payload.compoQuestions = [];
        }

        const response = await updateCompositionWithQuestions(payload);

        if (response.data.code === 200) {
          success("Composition modifiée avec succès !");
          resetForm();
          router.push({ name: "ListeCompositionPage" });
        }
      } catch (err: any) {
        console.error("Erreur dans editComposition :", err);
        let errorMessage = "Erreur lors de la modification de la composition";
        if (err.response?.status === 400) {
          errorMessage = err.response.data?.message || "Données invalides";
        }
        error(errorMessage);
      } finally {
        isSubmitting.value = false;
      }
    };

    const updateCompositionWithQuestions = async (payload: any) => {
      if (!payload.compoQuestions) {
        payload.compoQuestions = []; // ✅ garantie de non-undefined
      }

      if (payload.questionsToCreate && payload.questionsToCreate.length > 0) {
        const questionsToCreate = payload.questionsToCreate;
        delete payload.questionsToCreate;

        const createdQuestionsIds: number[] = [];
        for (const questionData of questionsToCreate) {
          try {
            const questionResponse = await ApiService.post('/questions', questionData);
            if (questionResponse.data.code === 201) {
              createdQuestionsIds.push(questionResponse.data.data.id);
            }
          } catch (err) {
            console.error("Erreur lors de la création d'une question:", err);
            throw err;
          }
        }

        payload.compoQuestions.push(...createdQuestionsIds.map(id => ({ question: { id } })));
      }

      if (payload.questionsToUpdate && payload.questionsToUpdate.length > 0) {
        const questionsToUpdate = payload.questionsToUpdate;
        delete payload.questionsToUpdate;

        for (const questionData of questionsToUpdate) {
          try {
            await ApiService.put(`/questions/${questionData.id}`, {
              contenu: questionData.contenu,
              reponse: questionData.reponse
            });
          } catch (err) {
            console.error("Erreur lors de la mise à jour d'une question:", err);
          }
        }

        // ✅ Ajouter les questions mises à jour à compoQuestions
        payload.compoQuestions.push(...questionsToUpdate.map((q: any) => ({ question: { id: q.id } })));
      }

      if (payload.questionsToDelete && payload.questionsToDelete.length > 0) {
        for (const questionId of payload.questionsToDelete) {
          try {
            await ApiService.delete(`/questions/${questionId}`);
          } catch (err) {
            console.error(`Erreur lors de la suppression de la question ${questionId}:`, err);
          }
        }
        delete payload.questionsToDelete;
      }

      return await ApiService.put(`/compositions/${route.params.id}`, payload);
    };

    const previewComposition = (id?: number) => {
      if (id) {
        const url = `/preview-composition/${id}`;
        window.open(url, '_blank');
      }
    };

    const closeModal = () => {
      const modalEl = document.getElementById("PreviewOrActiveCompositionModal");
      if (modalEl) {
        hideModal(modalEl);
      }
      router.push({ name: "ListeCompositionPage" });
    };

    return {
      compositionSchema,
      compositionForm,
      titre,
      dateComposition,
      dateFinSoumission,
      typeComposition,
      professeursOptions,
      niveauxOptions,
      matieresOptions,
      filieresOptions,
      anneesAcademiquesOptions,
      questionsOptions,
      selectedQuestions,
      addedQuestions,
      isSubmitting,
      selectedNiveau,
      selectedFiliere,
      selectedMatiere,
      selectedProfesseur,
      selectedAnneeAcademique,
      isLoadingMatieres,
      isLoadingFilieres,
      currentProfesseursOptions,
      handleInvalidSubmit,
      handleTypeChange,
      onQCMQuestionsChange,
      editComposition,
      previewComposition,
      closeModal,
      removeQuestion,
      addNewQuestion,
      onNiveauChange,
      onFiliereChange,
      onMatiereChange,
      getProfesseursByMatiere,
      updatedComposition,
      dureeMinutes
    };
  },
});

</script>

<style scoped>
@import '@vueform/multiselect/themes/default.css';

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.alert {
  border-radius: 0.5rem;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.text-primary {
  color: #007bff !important;
}

.text-success {
  color: #28a745 !important;
}

.border-primary {
  border-color: #007bff !important;
}

.border-success {
  border-color: #28a745 !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.form-check-input.me-2 {
  margin-right: 0.75rem;
}
</style>